import React, { useState, useEffect } from 'react';
import { useNotification } from './NotificationContext';
import { getUsersData, buildTree } from '../data/users';
import CurrencySelector from './CurrencySelector';
import { fetchAuthSession } from 'aws-amplify/auth';
import { notificationService } from '../services/notificationService';
import { getFullChain, refreshSelfData } from '../services/userSelf';
import useTranslation from '../services/translationService';
import { 
  XMarkIcon, 
  UserIcon, 
  KeyIcon,
  UserGroupIcon,
  StarIcon,
  BuildingStorefrontIcon,
  GlobeAsiaAustraliaIcon,
  AtSymbolIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import { searchUsers } from '../data/users';

const USER_HIERARCHY_LEVELS = {
  'admin': 6,
  'country': 5,
  'city': 4,
  'super-agent': 3,
  'agent': 2,
  'player': 1
};

export default function CreateUserModal({ isOpen, onClose, onSubmit, error: externalError, isLoading: externalLoading }) {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showNameField, setShowNameField] = useState(false);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState([]);
  const [userType, setUserType] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    group: '',
    password: '',
    parentId: ''
  });

  // Define agent types with translations
  const AGENT_TYPES = [
    { 
      id: 'Players', 
      name: t('userTypes.player', 'Player'),
      icon: UserIcon,
      color: 'text-gray-500',
      bgColor: 'bg-gray-600',
      bgHover: 'hover:bg-gray-700',
      bgDarkHover: 'dark:hover:bg-gray-800',
      bgSelected: 'bg-gray-50 dark:bg-gray-900/20',
      parentType: 'agent',
      description: t('createUser.playerDescription', 'Add a new player under an agent')
    },
    { 
      id: 'Agents', 
      name: t('userTypes.agent', 'Agent'),
      icon: UserGroupIcon,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-600',
      bgHover: 'hover:bg-yellow-700',
      bgDarkHover: 'dark:hover:bg-yellow-800',
      bgSelected: 'bg-yellow-50 dark:bg-yellow-900/20',
      parentType: 'super-agent'
    },
    { 
      id: 'SuperAgents', 
      name: t('userTypes.super-agent', 'Super Agent'),
      icon: StarIcon,
      color: 'text-orange-400',
      bgColor: 'bg-orange-600',
      bgHover: 'hover:bg-orange-700',
      bgDarkHover: 'dark:hover:bg-orange-800',
      bgSelected: 'bg-orange-50 dark:bg-orange-900/20',
      parentType: 'city'
    },
    { 
      id: 'Cities', 
      name: t('userTypes.city', 'City'),
      icon: BuildingStorefrontIcon,
      color: 'text-green-500',
      bgColor: 'bg-green-600',
      bgHover: 'hover:bg-green-700',
      bgDarkHover: 'dark:hover:bg-green-800',
      bgSelected: 'bg-green-50 dark:bg-green-900/20',
      parentType: 'country'
    },
    { 
      id: 'Countries', 
      name: t('userTypes.country', 'Country'),
      icon: GlobeAsiaAustraliaIcon,
      color: 'text-blue-500',
      bgColor: 'bg-blue-600',
      bgHover: 'hover:bg-blue-700',
      bgDarkHover: 'dark:hover:bg-blue-800',
      bgSelected: 'bg-blue-50 dark:bg-blue-900/20',
      parentType: 'admin'
    }
  ];

  // Map frontend type IDs to backend type names
  const getBackendType = (typeId) => {
    const typeMapping = {
      'Players': 'player',
      'Agents': 'agent',
      'SuperAgents': 'super-agent',
      'Cities': 'city',
      'Countries': 'country'
    };
    return typeMapping[typeId] || typeId.toLowerCase();
  };

  // Currencies that are mutually exclusive for players
  // A player may hold at most ONE of these at a time, optionally combined with LBP
  const EXCLUSIVE_PLAYER_CURRENCIES = ['USD', 'TND', 'TRY', 'IQD', 'KWD'];

  const isPlayerCreation = selectedType?.id === 'Players';

  const handleCurrencyToggle = (currency) => {
    const isSelected = selectedCurrencies.includes(currency);

    if (isPlayerCreation) {
      // Player rules: at most one of USD/TND/TRY/IQD/KWD, optionally plus LBP
      if (isSelected) {
        // Prevent removing the last selected currency
        if (selectedCurrencies.length > 1) {
          setSelectedCurrencies(selectedCurrencies.filter(c => c !== currency));
        }
        return;
      }

      if (EXCLUSIVE_PLAYER_CURRENCIES.includes(currency)) {
        // Replace any other exclusive currency, keep LBP if present
        const withoutExclusive = selectedCurrencies.filter(
          c => !EXCLUSIVE_PLAYER_CURRENCIES.includes(c)
        );
        setSelectedCurrencies([...withoutExclusive, currency]);
        return;
      }

      if (currency === 'LBP') {
        setSelectedCurrencies([...selectedCurrencies, currency]);
        return;
      }

      // Any other currency: not allowed alongside existing selections for players
      setSelectedCurrencies([currency]);
      return;
    }

    // Default (non-player) behavior: free multi-select, keep at least one
    if (isSelected) {
      if (selectedCurrencies.length > 1) {
        setSelectedCurrencies(selectedCurrencies.filter(c => c !== currency));
      }
    } else {
      setSelectedCurrencies([...selectedCurrencies, currency]);
    }
  };

  const [internalError, setInternalError] = useState('');
  const [internalLoading, setInternalLoading] = useState(false);
  
  // Use external props if provided, otherwise use internal state
  const error = externalError || internalError;
  const isLoading = externalLoading || internalLoading;

  // Fetch current user's type when modal opens
  useEffect(() => {
    const fetchUserType = async () => {
      try {
        const response = await getUsersData();
        if (response.success) {
          const tree = await buildTree(response.data, response.data.rootId);
          
          // Find the current user in the tree
          const findUser = (node) => {
            if (node.type) {
              return node;
            }
            if (node.children) {
              for (const child of node.children) {
                const found = findUser(child);
                if (found) return found;
              }
            }
            return null;
          };
          
          const currentUser = findUser(tree);
          if (currentUser) {
            setUserType(currentUser.type);
          } else {
            throw new Error(t('createUser.errors.userNotFound', 'Could not find current user in tree'));
          }
        } else {
          throw new Error(t('createUser.errors.fetchFailed', 'Failed to fetch user data'));
        }
      } catch (error) {
        console.error('Error fetching user type:', error);
        showNotification(t('createUser.errors.determineType', 'Failed to determine user type'), 'error');
      }
    };

    if (isOpen) {
      fetchUserType();
    }
  }, [isOpen, showNotification, t]);

  // Filter agent types based on user's type
  const getFilteredAgentTypes = () => {
    if (!userType) return [];
    
    // If user is admin, they can create any type
    if (userType === 'admin') {
      return AGENT_TYPES;
    }

    // Define the hierarchy of who can create what
    const creationHierarchy = {
      'country': ['Cities'],
      'city': ['SuperAgents'],
      'super-agent': ['Agents', 'Players'],
      'agent': ['Players']
    };

    // Get the types the current user can create
    const allowedTypes = creationHierarchy[userType] || [];
    
    // Filter AGENT_TYPES to only include allowed types
    return AGENT_TYPES.filter(type => allowedTypes.includes(type.id));
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    // Only allow search for admin and country level users
    if (!['admin', 'country'].includes(userType)) {
      return;
    }

    if (query.length >= 2) {
      const searchResults = await searchUsers(query);
      // For players, show agents as potential parents, unless current user is super-agent
      const results = searchResults.filter(user => {
        if (selectedType.id === 'Players') {
          if (userType === 'super-agent') {
            return false; // Don't show any search results for super-agents creating players
          }
          return user.type === 'agent';
        }
        return user.type === selectedType.parentType;
      });
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleCurrencies = (user) => {
    const currencies = user.attributes?.currencies || user.currencies;
    if (currencies) {
      setAvailableCurrencies(currencies);
      // Default selection: prefer USD, otherwise first available currency.
      // This is safe for both player and non-player creation because a single
      // non-LBP currency is always a valid starting selection.
      setSelectedCurrencies(currencies.includes('USD') ? ['USD'] : [currencies[0]]);
      return true;
    } else {
      setAvailableCurrencies([]);
      setSelectedCurrencies([]);
      showNotification(t('createUser.errors.noCurrencies', 'No currencies available from parent'), 'warning');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isLoading || externalLoading) {
      console.log('Already submitting, ignoring duplicate form submission');
      return;
    }
    
    setInternalError('');
    setInternalLoading(true);

    const { username, password, name, group } = formData;

    // Basic validation
    if (!username || !password) {
      setInternalError(t('createUser.errors.requiredFields', 'Please fill in all required fields'));
      setInternalLoading(false);
      return;
    }
    
    // Username validation - only alphanumeric characters allowed
    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      setInternalError(t('createUser.errors.invalidUsername', 'Username can only contain letters and numbers'));
      setInternalLoading(false);
      return;
    }
    
    // Password validation
    if (password.length < 6) {
      setInternalError(t('auth.errors.passwordLength', 'Password must be at least 6 characters long'));
      setInternalLoading(false);
      return;
    }

    // Currency validation - moved up before any API calls
    if (!selectedCurrencies || selectedCurrencies.length === 0) {
      setInternalError(t('createUser.errors.selectCurrency', 'At least one currency must be selected'));
      setInternalLoading(false);
      return;
    }

    // Player-specific currency validation:
    // Players may hold at most ONE of USD/TND/TRY/IQD/KWD, optionally combined with LBP.
    if (getBackendType(selectedType.id) === 'player') {
      const exclusivePicked = selectedCurrencies.filter(c =>
        EXCLUSIVE_PLAYER_CURRENCIES.includes(c)
      );
      const hasLbp = selectedCurrencies.includes('LBP');
      const allowedSet = new Set([...EXCLUSIVE_PLAYER_CURRENCIES, 'LBP']);
      const hasOther = selectedCurrencies.some(c => !allowedSet.has(c));

      if (exclusivePicked.length > 1 || hasOther || selectedCurrencies.length > (hasLbp ? 2 : 1)) {
        setInternalError(
          t(
            'createUser.errors.invalidPlayerCurrencies',
            'Players can only have one of USD, TND, TRY, IQD, or KWD, optionally combined with LBP.'
          )
        );
        setInternalLoading(false);
        return;
      }
    }

    // Auto-fill name from username if name is empty
    const finalName = name || (username.charAt(0).toUpperCase() + username.slice(1));

    const createUserData = {
      username: username.toLowerCase(),
      password,
      name: finalName,
      type: getBackendType(selectedType.id),
      group: group || selectedType.id,
      parentId: selectedParent?.id,
      currencies: selectedCurrencies,
      attributes: {
        currencies: selectedCurrencies,
        creationDate: new Date().toISOString()
      }
    };

    console.log('CreateUserModal: submitting user data', {
      selectedTypeName: selectedType.name,
      selectedTypeId: selectedType.id,
      finalType: getBackendType(selectedType.id),
      createUserData
    });

    try {
      await onSubmit(createUserData);
      
      // Refresh the user tree to ensure we have the latest data
      await refreshSelfData();
      
      // Get the full chain for the new user
      const userChain = await getFullChain(username.toLowerCase());
      
      // Create notification message
      const userTypeDisplay = selectedType.name.toLowerCase();
      const notificationMessage = t('createUser.notifications.created', {
        type: userTypeDisplay,
        name: finalName,
        username: username.toLowerCase(),
        parent: selectedParent?.name || t('createUser.system', 'the system')
      }, `New ${userTypeDisplay} "${finalName}" (${username.toLowerCase()}) has been created under ${selectedParent?.name || 'the system'}`);
      
      // Send notification to everyone in the chain
      await notificationService.sendNotification({
        content: notificationMessage,
        recipients: userChain
      });
      
      showNotification(
        t('createUser.success.created', { type: selectedType.name.toLowerCase(), name: finalName }, 
          `Successfully created ${selectedType.name.toLowerCase()} ${finalName}`), 
        'success'
      );
      handleClose();
    } catch (error) {
      setInternalError(error.message || t('createUser.errors.failed', 'Failed to create user'));
    } finally {
      setInternalLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSearchQuery('');
    setSelectedParent(null);
    setSelectedType(null);
    setSearchResults([]);
    setAvailableCurrencies([]);
    setSelectedCurrencies([]);
    setShowNameField(false);
    setFormData({
      username: '',
      name: '',
      group: '',
      password: '',
      parentId: ''
    });
    setInternalError('');
    setInternalLoading(false);
    onClose();
  };

  // Add a useEffect to clear error when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset error state when modal closes
      setInternalError('');
      setInternalLoading(false);
    }
  }, [isOpen]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setFormData(prev => ({ ...prev, group: type.id }));
    
    // Check if the selected type is one level below the current user
    // Use the backend type mapping instead of the translated name
    const backendType = getBackendType(type.id);
    const selectedTypeLevel = USER_HIERARCHY_LEVELS[backendType];
    const userLevel = USER_HIERARCHY_LEVELS[userType];
    
    if (userLevel && selectedTypeLevel === userLevel - 1) {
      // Get the current user from the tree data
      getUsersData().then(async response => {
        if (response.success) {
          buildTree(response.data, response.data.rootId).then(async tree => {
            // Find the current user in the tree
            const findUser = (node) => {
              if (node.type === userType) {
                return node;
              }
              if (node.children) {
                for (const child of node.children) {
                  const found = findUser(child);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const currentUser = findUser(tree);
            if (currentUser) {
              setSelectedParent(currentUser);
              handleCurrencies(currentUser);
              setStep(3); // Skip the parent selection step
            } else {
              setSearchQuery('');
              setSearchResults([]);
              setStep(2); // Go to parent selection if current user not found
            }
          });
        } else {
          setSearchQuery('');
          setSearchResults([]);
          setStep(2);
        }
      }).catch(() => {
        setSearchQuery('');
        setSearchResults([]);
        setStep(2);
      });
    } else if (userType === 'super-agent' && type.id === 'Players') {
      // Special case: Super-agent creating a player
      getUsersData().then(async response => {
        if (response.success) {
          buildTree(response.data, response.data.rootId).then(async tree => {
            // Find the current super-agent
            const findSuperAgent = (node) => {
              if (node.type === 'super-agent') {
                return node;
              }
              if (node.children) {
                for (const child of node.children) {
                  const found = findSuperAgent(child);
                  if (found) return found;
                }
              }
              return null;
            };
            
            const superAgent = findSuperAgent(tree);
            if (superAgent) {
              setSelectedParent(superAgent);
              handleCurrencies(superAgent);
              setStep(3); // Skip parent selection, go straight to form
            }
          });
        }
      });
    } else {
      // Clear search state when changing types
      setSearchQuery('');
      setSearchResults([]);
      setStep(2);
    }
  };

  const handleParentSelect = async (parent) => {
    setSelectedParent(parent);
    handleCurrencies(parent);
    setStep(3);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For username field, only allow alphanumeric characters
    if (name === 'username') {
      // Remove any non-alphanumeric characters from the input
      const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: cleanedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  if (!isOpen) return null;

  const filteredAgentTypes = getFilteredAgentTypes();

  // Helper function to get parent type display name
  const getParentTypeDisplay = (parentType) => {
    if (parentType === 'super-agent') return t('userTypes.super-agent', 'Super Agent');
    if (parentType === 'city') return t('userTypes.city', 'City');
    if (parentType === 'country') return t('userTypes.country', 'Country');
    if (parentType === 'agent') return t('userTypes.agent', 'Agent');
    return t(`userTypes.${parentType}`, parentType);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 mb-16">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {selectedType && (
              <selectedType.icon className={`h-6 w-6 ${selectedType.color}`} />
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {step === 1 ? t('createUser.selectType', 'Select Agent Type') : 
                 step === 2 ? t('createUser.selectParent', {type: getParentTypeDisplay(selectedType?.parentType)}, 
                               `Select Parent ${getParentTypeDisplay(selectedType?.parentType)}`) :
                 t('createUser.createNew', {type: selectedType?.name}, `Create New ${selectedType?.name}`)}
              </h2>
              {step === 2 && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('createUser.parentRequirement', 
                    {type: selectedType?.name.toLowerCase(), parentType: getParentTypeDisplay(selectedType?.parentType)},
                    `A ${selectedType?.name.toLowerCase()} must be assigned to a ${getParentTypeDisplay(selectedType?.parentType)} who will manage them`
                  )}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            aria-label={t('common.close')}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {step === 1 && (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 gap-4">
              {/* Player option - larger and more prominent */}
              {filteredAgentTypes.filter(type => type.id === 'Players').map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleTypeSelect(type)}
                    className={`flex items-center gap-4 p-6 rounded-lg border-2 border-gray-300 dark:border-gray-600 
                      ${selectedType?.id === type.id ? type.bgSelected : 'bg-white dark:bg-gray-800'} 
                      ${type.bgHover} transition-all`}
                  >
                    <div className={`p-3 rounded-full ${selectedType?.id === type.id ? type.bgSelected : 'bg-white dark:bg-gray-800'}`}>
                      <Icon className={`h-8 w-8 ${type.color}`} />
                    </div>
                    <div className="text-left">
                      <div className="text-lg font-medium">{type.name}</div>
                      {type.description && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {type.description}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
              
              {/* Other options in a grid */}
              <div className="grid grid-cols-2 gap-3">
                {filteredAgentTypes.filter(type => type.id !== 'Players').map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleTypeSelect(type)}
                      className={`flex items-center gap-3 p-4 rounded-lg border border-gray-300 dark:border-gray-600 
                        ${selectedType?.id === type.id ? type.bgSelected : 'bg-white dark:bg-gray-800'} 
                        ${type.bgHover} transition-all`}
                    >
                      <Icon className={`h-6 w-6 ${type.color}`} />
                      <span className="text-sm font-medium">{type.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder={t('createUser.searchParent', 
                    {type: getParentTypeDisplay(selectedType?.parentType)},
                    `Search for an ${getParentTypeDisplay(selectedType?.parentType)} by name...`
                  )}
                  className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
              
              <div className="space-y-2">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleParentSelect(result)}
                    className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <UserGroupIcon className="h-5 w-5 text-gray-400" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {result.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {result.username}
                        </div>
                      </div>
                    </div>
                    <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-4">
              {selectedParent && (
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    {t('createUser.parent', 'Parent')}: <span className="font-medium">{selectedParent.name}</span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2">({selectedParent.username})</span>
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('auth.username', 'Username')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      required
                      value={formData.username}
                      onChange={handleChange}
                      placeholder={t('createUser.usernamePlaceholder', 'e.g., johnsmith123 (letters and numbers only)')}
                      className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('auth.password', 'Password')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={t('createUser.passwordPlaceholder', 'Minimum 6 characters')}
                      className="pl-10 appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm dark:bg-gray-700 dark:text-white pr-12"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <button
                        type="button"
                        className="px-3 py-2 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('createUser.allowedCurrencies', 'Allowed Currencies')} <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {availableCurrencies.map((currency) => (
                        <button
                          key={currency}
                          type="button"
                          onClick={() => handleCurrencyToggle(currency)}
                          className={`
                            inline-flex items-center px-4 py-2 rounded-full border-2 transition-all
                            ${selectedCurrencies.includes(currency)
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/50 dark:text-blue-300'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:border-gray-500'}
                          `}
                        >
                          <span className="text-base font-medium">{currency}</span>
                          {selectedCurrencies.includes(currency) && (
                            <span className="ml-2 text-sm bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full w-5 h-5 inline-flex items-center justify-center">
                              ✓
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    {isPlayerCreation && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t(
                          'createUser.playerCurrencyRule',
                          'Players can have only one of USD, TND, TRY, IQD, or KWD. LBP can be added as a second currency.'
                        )}
                      </p>
                    )}
                    {selectedCurrencies.length === 0 && (
                      <p className="text-sm text-red-500 dark:text-red-400">
                        {t('createUser.errors.selectCurrency', 'Please select at least one currency')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* More info section */}
            <div className="mt-4">
              {!showNameField ? (
                <button
                  type="button"
                  onClick={() => setShowNameField(true)}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
                >
                  <ChevronRightIcon className="h-3 w-3" />
                  {t('createUser.moreInfo', 'More info')}
                </button>
              ) : (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNameField(false);
                      setFormData(prev => ({ ...prev, name: '' }));
                    }}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center gap-1"
                  >
                    <ChevronRightIcon className="h-3 w-3 rotate-90" />
                    {t('createUser.lessInfo', 'Less info')}
                  </button>
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {t('users.name', 'Name')} <span className="text-gray-500 font-normal">({t('createUser.optional', 'Optional')})</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('createUser.namePlaceholder', 'e.g., John Smith (will auto-fill from username if empty)')}
                        className="pl-8 appearance-none block w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/50 text-red-500 dark:text-red-200 text-sm p-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                  ${selectedType?.bgColor}
                  ${selectedType?.bgHover}
                  ${selectedType?.bgDarkHover}
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${selectedType?.color.split('-')[1]} 
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading 
                  ? t('createUser.creating', 'Creating...') 
                  : t('createUser.createType', {type: selectedType?.name}, `Create ${selectedType?.name}`)}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
} 